// Every question Orbit can ask, each with its own code. Changed by pull
// request, never in the app.
import type { CodeSystem } from '@medplum/fhirtypes'
import { ASSESSMENT_TEMPLATE_QUESTION_CONCEPTS } from '#src/terminology/assessmentTemplates.ts'
import { INSTRUMENT_QUESTION_CONCEPTS } from '#src/terminology/instruments/instrumentTerminology.ts'
import { NUTRITION_QUESTION_CONCEPTS } from '#src/terminology/nutrition.ts'
import { PCP_QUESTION_CONCEPTS } from '#src/terminology/pcpInitialAssessment.ts'
import { RD_QUESTION_CONCEPTS } from '#src/terminology/rdInitialAssessment.ts'
import { VITAL_QUESTION_CONCEPTS } from '#src/terminology/vitals.ts'

export const ASSESSMENT_QUESTION_CODES: CodeSystem = {
  resourceType: 'CodeSystem',
  url: 'https://www.seenhealth.org/fhir/CodeSystem/assessment-questions',
  version: '1',
  name: 'SeenAssessmentQuestions',
  title: 'Assessment Questions',
  status: 'active',
  content: 'complete',
  caseSensitive: true,
  meta: {
    tag: [
      {
        system: 'https://www.seenhealth.org/fhir/managed-by',
        code: 'code',
      },
    ],
  },
  property: [
    {
      code: 'loinc-related',
      type: 'Coding',
    },
    {
      code: 'lookback',
      type: 'string',
    },
    {
      code: 'domain',
      type: 'code',
    },
    {
      code: 'binding',
      type: 'string',
    },
    {
      code: 'prefill',
      type: 'boolean',
    },
    {
      code: 'observation-extract',
      type: 'boolean',
    },
    {
      code: 'reference-resource-type',
      type: 'code',
    },
    {
      code: 'managed-by',
      type: 'code',
    },
    // How a code is recorded on its own as an observation, named as LOINC
    // names them so our codes resolve through the same lookup.
    { code: 'unit', type: 'string' },
    { code: 'SCALE_TYP', type: 'string' },
    { code: 'answer-list', type: 'string' },
    { code: 'value-kind', type: 'code' },
    { code: 'repeats', type: 'boolean' },
  ],
  concept: [
    {
      code: 'oral-med-management-1wk-brief-setup',
      display:
        'Self-administers all prescribed oral medications reliably and safely, previous 1 week',
      definition:
        'In the previous 1 week, have you been able to self-administer (prepare and take) all prescribed oral medications reliably and safely, including administration of the correct dosages at the appropriate times/intervals (exclude injectable, inhalant/mist, and IV medications)?',
      property: [
        {
          code: 'loinc-related',
          valueCoding: {
            system: 'http://loinc.org',
            code: '57285-9',
          },
        },
        {
          code: 'lookback',
          valueString: 'P1W',
        },
        {
          code: 'domain',
          valueCode: 'medications',
        },
        {
          code: 'observation-extract',
          valueBoolean: true,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString:
            'https://www.seenhealth.org/fhir/ValueSet/oral-med-management-answers',
        },
      ],
    },
    {
      code: 'bladder-control-3-level',
      display: 'Bladder control',
      definition: 'Bladder control',
      property: [
        {
          code: 'loinc-related',
          valueCoding: {
            system: 'http://loinc.org',
            code: '96760-4',
          },
        },
        {
          code: 'domain',
          valueCode: 'physical-function',
        },
        {
          code: 'observation-extract',
          valueBoolean: true,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString:
            'https://www.seenhealth.org/fhir/ValueSet/bladder-control-answers',
        },
      ],
    },
    {
      code: 'idt-member',
      display: 'IDT member',
      definition: 'IDT Member completing assessment',
      property: [
        {
          code: 'domain',
          valueCode: 'overview',
        },
        {
          code: 'reference-resource-type',
          valueCode: 'Practitioner',
        },
        {
          code: 'binding',
          valueString: 'practitioner.reference',
        },
        {
          code: 'prefill',
          valueBoolean: true,
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Ref' },
      ],
    },
    {
      code: 'assessment-date',
      display: 'Assessment date',
      definition: 'Date Assessment Completed:',
      property: [
        {
          code: 'domain',
          valueCode: 'overview',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Qn' },
        { code: 'value-kind', valueCode: 'date' },
      ],
    },
    {
      code: 'mode',
      display: 'Assessment mode',
      definition: 'Mode:',
      property: [
        {
          code: 'domain',
          valueCode: 'overview',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString:
            'https://www.seenhealth.org/fhir/ValueSet/assessment-mode',
        },
      ],
    },
    {
      code: 'location',
      display: 'Assessment location',
      definition: 'Location',
      property: [
        {
          code: 'domain',
          valueCode: 'overview',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString:
            'https://www.seenhealth.org/fhir/ValueSet/assessment-location',
        },
      ],
    },
    {
      code: 'current-address',
      display: 'Current address',
      definition: 'Current Address:',
      property: [
        {
          code: 'domain',
          valueCode: 'overview',
        },
        {
          code: 'binding',
          valueString: 'participant.home-address-line',
        },
        {
          code: 'prefill',
          valueBoolean: true,
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nar' },
      ],
    },
    {
      code: 'other-participants',
      display: 'Other participants',
      definition: 'Other participants (if applicable)',
      property: [
        {
          code: 'domain',
          valueCode: 'overview',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'reference-resource-type',
          valueCode: 'Patient',
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'interpreter',
      display: 'Interpreter',
      definition: 'Interpreter / Translation:',
      property: [
        {
          code: 'domain',
          valueCode: 'overview',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString:
            'https://www.seenhealth.org/fhir/ValueSet/interpreter-need',
        },
      ],
    },
    {
      code: 'interpreter-source',
      display: 'Interpreter source',
      definition: 'Who interpreted?',
      property: [
        {
          code: 'domain',
          valueCode: 'overview',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString:
            'https://www.seenhealth.org/fhir/ValueSet/interpreter-source',
        },
      ],
    },
    {
      code: 'interpreter-organization',
      display: 'Interpreter organization',
      definition: 'Interpreter service:',
      property: [
        {
          code: 'domain',
          valueCode: 'overview',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'reference-resource-type',
          valueCode: 'Organization',
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Ref' },
      ],
    },
    {
      code: 'interpreter-practitioner',
      display: 'Interpreter practitioner',
      definition: 'Interpreter:',
      property: [
        {
          code: 'domain',
          valueCode: 'overview',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'reference-resource-type',
          valueCode: 'Practitioner',
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Ref' },
      ],
    },
    {
      code: 'interpreter-related-person',
      display: 'Interpreter contact',
      definition: 'Family or caregiver interpreter:',
      property: [
        {
          code: 'domain',
          valueCode: 'overview',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'reference-resource-type',
          valueCode: 'RelatedPerson',
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Ref' },
      ],
    },
    {
      code: 'interpreter-session',
      display: 'Interpreter session',
      definition: 'Interpreter session ID:',
      property: [
        {
          code: 'domain',
          valueCode: 'overview',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nar' },
      ],
    },
    {
      code: 'interpreter-note',
      display: 'Interpreter note',
      definition: 'Interpreter note:',
      property: [
        {
          code: 'domain',
          valueCode: 'overview',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nar' },
      ],
    },
    {
      code: 'interpreter-language',
      display: 'Interpreter language',
      definition: 'Language:',
      property: [
        {
          code: 'domain',
          valueCode: 'overview',
        },
        {
          code: 'binding',
          valueString: 'participant.languages',
        },
        {
          code: 'prefill',
          valueBoolean: true,
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nar' },
      ],
    },
    {
      code: 'participant-priorities',
      display: 'Priorities of care',
      definition: 'Participant Priorities of Care (enter details)',
      property: [
        {
          code: 'domain',
          valueCode: 'priorities',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nar' },
      ],
    },
    {
      code: 'clear-pathways',
      display: 'Clear pathways',
      definition: 'Are there clear pathways to move through the house?',
      property: [
        {
          code: 'domain',
          valueCode: 'environment',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString: 'https://www.seenhealth.org/fhir/ValueSet/yes-no',
        },
      ],
    },
    {
      code: 'flooring-hazards',
      display: 'Flooring hazards',
      definition: 'Are there home hazards with flooring/carpeting?',
      property: [
        {
          code: 'domain',
          valueCode: 'environment',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString: 'https://www.seenhealth.org/fhir/ValueSet/yes-no',
        },
      ],
    },
    {
      code: 'flooring-hazard-details',
      display: 'Flooring hazard details',
      definition: 'Which flooring/carpeting hazards are present?',
      property: [
        {
          code: 'domain',
          valueCode: 'environment',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'bathroom-hazards',
      display: 'Bathroom hazards',
      definition: 'Are there home hazards in the bathroom/toilet room?',
      property: [
        {
          code: 'domain',
          valueCode: 'environment',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString: 'https://www.seenhealth.org/fhir/ValueSet/yes-no',
        },
      ],
    },
    {
      code: 'bathroom-hazard-details',
      display: 'Bathroom hazard details',
      definition: 'Which bathroom/toilet hazards are present?',
      property: [
        {
          code: 'domain',
          valueCode: 'environment',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'bedroom-hazards',
      display: 'Bedroom hazards',
      definition: 'Are there home hazards in the bedroom?',
      property: [
        {
          code: 'domain',
          valueCode: 'environment',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString: 'https://www.seenhealth.org/fhir/ValueSet/yes-no',
        },
      ],
    },
    {
      code: 'bedroom-hazard-details',
      display: 'Bedroom hazard details',
      definition: 'Which bedroom hazards are present?',
      property: [
        {
          code: 'domain',
          valueCode: 'environment',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'kitchen-hazards',
      display: 'Kitchen hazards',
      definition: 'Are there home hazards in the kitchen?',
      property: [
        {
          code: 'domain',
          valueCode: 'environment',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString: 'https://www.seenhealth.org/fhir/ValueSet/yes-no',
        },
      ],
    },
    {
      code: 'kitchen-hazard-details',
      display: 'Kitchen hazard details',
      definition: 'Which kitchen hazards are present?',
      property: [
        {
          code: 'domain',
          valueCode: 'environment',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'cold-storage',
      display: 'Cold food storage',
      definition: 'Is there improper/ unavailable cold food storage?',
      property: [
        {
          code: 'domain',
          valueCode: 'environment',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString: 'https://www.seenhealth.org/fhir/ValueSet/yes-no',
        },
      ],
    },
    {
      code: 'cold-storage-details',
      display: 'Cold storage details',
      definition: 'Which cold food storage issues are present?',
      property: [
        {
          code: 'domain',
          valueCode: 'environment',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'heating-hazards',
      display: 'Heating and cooling hazards',
      definition: 'Are there home hazards with heating/cooling?',
      property: [
        {
          code: 'domain',
          valueCode: 'environment',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString: 'https://www.seenhealth.org/fhir/ValueSet/yes-no',
        },
      ],
    },
    {
      code: 'heating-hazard-details',
      display: 'Heating hazard details',
      definition: 'Which heating/cooling hazards are present?',
      property: [
        {
          code: 'domain',
          valueCode: 'environment',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'personal-safety',
      display: 'Personal safety',
      definition: 'Are there personal safety issues?',
      property: [
        {
          code: 'domain',
          valueCode: 'environment',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString: 'https://www.seenhealth.org/fhir/ValueSet/yes-no',
        },
      ],
    },
    {
      code: 'personal-safety-details',
      display: 'Personal safety details',
      definition: 'Which personal safety issues are present?',
      property: [
        {
          code: 'domain',
          valueCode: 'environment',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'home-access',
      display: 'Home access',
      definition: 'Are there home access issues?',
      property: [
        {
          code: 'domain',
          valueCode: 'environment',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString: 'https://www.seenhealth.org/fhir/ValueSet/yes-no',
        },
      ],
    },
    {
      code: 'home-access-details',
      display: 'Home access details',
      definition: 'Which home access issues are present?',
      property: [
        {
          code: 'domain',
          valueCode: 'environment',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'pet-issues',
      display: 'Pet issues',
      definition: 'Are there issues with pet(s) in the home?',
      property: [
        {
          code: 'domain',
          valueCode: 'environment',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString: 'https://www.seenhealth.org/fhir/ValueSet/yes-no',
        },
      ],
    },
    {
      code: 'pet-issue-details',
      display: 'Pet issue details',
      definition: 'Which pet issues are present?',
      property: [
        {
          code: 'domain',
          valueCode: 'environment',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'infestations',
      display: 'Infestations',
      definition: 'Are there bug infestations in the home?',
      property: [
        {
          code: 'domain',
          valueCode: 'environment',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString: 'https://www.seenhealth.org/fhir/ValueSet/yes-no',
        },
      ],
    },
    {
      code: 'infestation-details',
      display: 'Infestation details',
      definition: 'Which bug or rodent infestations are present?',
      property: [
        {
          code: 'domain',
          valueCode: 'environment',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'smoke-alarms',
      display: 'Smoke alarms',
      definition: 'Are there operational smoke alarms in the home?',
      property: [
        {
          code: 'domain',
          valueCode: 'environment',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString: 'https://www.seenhealth.org/fhir/ValueSet/yes-no',
        },
      ],
    },
    {
      code: 'co-alarms',
      display: 'Carbon monoxide alarms',
      definition: 'Are there operational carbon monoxide alarms in the home?',
      property: [
        {
          code: 'domain',
          valueCode: 'environment',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString: 'https://www.seenhealth.org/fhir/ValueSet/yes-no',
        },
      ],
    },
    {
      code: 'daily-decisions',
      display: 'Daily decisions',
      definition:
        'How well did the participant make decisions about organizing the day (e.g., when to get up or have meals, which clothes to wear or activities to do)?',
      property: [
        {
          code: 'loinc-related',
          valueCoding: {
            system: 'http://loinc.org',
            code: '45490-0',
          },
        },
        {
          code: 'domain',
          valueCode: 'environment',
        },
        {
          code: 'observation-extract',
          valueBoolean: true,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString:
            'https://www.seenhealth.org/fhir/ValueSet/daily-decisions',
        },
      ],
    },
    {
      code: 'pers',
      display: 'Personal emergency response',
      definition: 'Does participant need a Personal Emergency Response System?',
      property: [
        {
          code: 'domain',
          valueCode: 'emergency',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString: 'https://www.seenhealth.org/fhir/ValueSet/yes-no',
        },
      ],
    },
    {
      code: 'phone-alone',
      display: 'Phone use when alone',
      definition:
        'Is he/she consistently and reliably able to use the phone when left alone?',
      property: [
        {
          code: 'domain',
          valueCode: 'emergency',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString: 'https://www.seenhealth.org/fhir/ValueSet/yes-no',
        },
      ],
    },
    {
      code: 'continuous-assistance',
      display: 'Continuous assistance',
      definition: 'Does the participant require 24/7 assistance?',
      property: [
        {
          code: 'domain',
          valueCode: 'emergency',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString: 'https://www.seenhealth.org/fhir/ValueSet/yes-no',
        },
      ],
    },
    {
      code: 'continuous-assistance-reason',
      display: 'Continuous assistance reason',
      definition: 'Why the participant requires 24/7 assistance',
      property: [
        {
          code: 'domain',
          valueCode: 'emergency',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nar' },
      ],
    },
    {
      code: 'bladder-control-4-level',
      display: 'Control of bladder function',
      definition: 'Control of bladder function:',
      property: [
        {
          code: 'loinc-related',
          valueCoding: {
            system: 'http://loinc.org',
            code: '46553-4',
          },
        },
        {
          code: 'domain',
          valueCode: 'continence',
        },
        {
          code: 'observation-extract',
          valueBoolean: true,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString:
            'https://www.seenhealth.org/fhir/ValueSet/bladder-control-4-level',
        },
      ],
    },
    {
      code: 'bowel-control',
      display: 'Bowel control',
      definition: 'Control of bowel function:',
      property: [
        {
          code: 'loinc-related',
          valueCoding: {
            system: 'http://loinc.org',
            code: '46587-2',
          },
        },
        {
          code: 'domain',
          valueCode: 'continence',
        },
        {
          code: 'observation-extract',
          valueBoolean: true,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString: 'https://www.seenhealth.org/fhir/ValueSet/bowel-control',
        },
      ],
    },
    {
      code: 'incontinence-supplies',
      display: 'Incontinence supplies',
      definition:
        'Use of incontinence supplies (e.g., pads, briefs, liners, wipes, urinals, bed pans, gloves):',
      property: [
        {
          code: 'domain',
          valueCode: 'continence',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString: 'https://www.seenhealth.org/fhir/ValueSet/yes-no',
        },
      ],
    },
    {
      code: 'oral-med-management-1wk-itemized-setup',
      display: 'Oral medication management, previous 1 week',
      definition:
        'In the previous 1 week, have you been able to self-administer (prepare and talk) all prescribed oral medications reliably and safely, including administration of the correct dosages at the appropriate times/intervals (exclude injectable, inhalant/mist, and IV medications)?',
      property: [
        {
          code: 'loinc-related',
          valueCoding: {
            system: 'http://loinc.org',
            code: '57285-9',
          },
        },
        {
          code: 'lookback',
          valueString: 'P1W',
        },
        {
          code: 'domain',
          valueCode: 'medications',
        },
        {
          code: 'observation-extract',
          valueBoolean: true,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString:
            'https://www.seenhealth.org/fhir/ValueSet/oral-med-management-itemized-setup',
        },
      ],
    },
    {
      code: 'edu-environment',
      display: 'Environment education',
      definition:
        'Education was provided to the participant and/or caregiver on the following, all questions were answered along with knowledge check: Environmental Assessment',
      property: [
        {
          code: 'domain',
          valueCode: 'education',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString: 'https://www.seenhealth.org/fhir/ValueSet/yes-no',
        },
      ],
    },
    {
      code: 'edu-emergency',
      display: 'Emergency education',
      definition:
        'Education was provided to the participant and/or caregiver on the following, all questions were answered along with knowledge check: Emergency',
      property: [
        {
          code: 'domain',
          valueCode: 'education',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString: 'https://www.seenhealth.org/fhir/ValueSet/yes-no',
        },
      ],
    },
    {
      code: 'edu-continence',
      display: 'Continence education',
      definition:
        'Education was provided to the participant and/or caregiver on the following, all questions were answered along with knowledge check: Continence',
      property: [
        {
          code: 'domain',
          valueCode: 'education',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString: 'https://www.seenhealth.org/fhir/ValueSet/yes-no',
        },
      ],
    },
    {
      code: 'edu-adl',
      display: 'ADL education',
      definition:
        'Education was provided to the participant and/or caregiver on the following, all questions were answered along with knowledge check: Activities of daily Living',
      property: [
        {
          code: 'domain',
          valueCode: 'education',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString: 'https://www.seenhealth.org/fhir/ValueSet/yes-no',
        },
      ],
    },
    {
      code: 'edu-equipment',
      display: 'Equipment education',
      definition:
        'Education was provided to the participant and/or caregiver on the following, all questions were answered along with knowledge check: Equipment Management',
      property: [
        {
          code: 'domain',
          valueCode: 'education',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString: 'https://www.seenhealth.org/fhir/ValueSet/yes-no',
        },
      ],
    },
    {
      code: 'edu-medication',
      display: 'Medication education',
      definition:
        'Education was provided to the participant and/or caregiver on the following, all questions were answered along with knowledge check: Medication',
      property: [
        {
          code: 'domain',
          valueCode: 'education',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString: 'https://www.seenhealth.org/fhir/ValueSet/yes-no',
        },
      ],
    },
    {
      code: 'learning-barriers',
      display: 'Learning barriers',
      definition: 'What are learning barrier(s) for the participant?',
      property: [
        {
          code: 'domain',
          valueCode: 'education',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'education-recipients',
      display: 'Education recipients',
      definition:
        'Who received education at today’s assessment? (check all that apply)',
      property: [
        {
          code: 'domain',
          valueCode: 'education',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'learning-demonstration',
      display: 'Learning demonstration',
      definition:
        'How did the participant/family member/caregiver demonstrate learning? (check all that apply)',
      property: [
        {
          code: 'domain',
          valueCode: 'education',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'cna-caregiver',
      display: 'CNA caregiver',
      definition: 'Does the participant require a caregiver with a CNA?',
      property: [
        {
          code: 'domain',
          valueCode: 'care-plan',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString: 'https://www.seenhealth.org/fhir/ValueSet/yes-no',
        },
      ],
    },
    {
      code: 'services-before-idt',
      display: 'Services before IDT approval',
      definition:
        'Is the participant in need of home care services before the initial care plan is approved by IDT?',
      property: [
        {
          code: 'domain',
          valueCode: 'care-plan',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString: 'https://www.seenhealth.org/fhir/ValueSet/yes-no',
        },
      ],
    },
    {
      code: 'dme-owned',
      display: 'DME owned',
      definition: 'DME Owned:',
      property: [
        {
          code: 'domain',
          valueCode: 'care-plan',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nar' },
      ],
    },
    {
      code: 'home-environment',
      display: 'Home environment',
      definition: 'Description of Home Environment:',
      property: [
        {
          code: 'domain',
          valueCode: 'care-plan',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nar' },
      ],
    },
    {
      code: 'goals-indicated',
      display: 'Care plan indicated',
      definition:
        'Are care plan goals and interventions indicated at this time?',
      property: [
        {
          code: 'domain',
          valueCode: 'care-plan',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString: 'https://www.seenhealth.org/fhir/ValueSet/yes-no',
        },
      ],
    },
    {
      code: 'recommended-modifications',
      display: 'Recommended modifications or DME',
      definition: 'Recommended House Modifications or DME:',
      property: [
        {
          code: 'domain',
          valueCode: 'care-plan',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nar' },
      ],
    },
    {
      code: 'priorities-aligned',
      display: 'Aligned with priorities',
      definition:
        'Are care plan goals and interventions aligned with the PPT priorities of care?',
      property: [
        {
          code: 'domain',
          valueCode: 'care-plan',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString: 'https://www.seenhealth.org/fhir/ValueSet/yes-no',
        },
      ],
    },
    {
      code: 'ppt-approved',
      display: 'Participant approved interventions',
      definition:
        'Did the PPT or designated representative approve the recommended interventions?',
      property: [
        {
          code: 'domain',
          valueCode: 'care-plan',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nom' },
        {
          code: 'answer-list',
          valueString: 'https://www.seenhealth.org/fhir/ValueSet/yes-no',
        },
      ],
    },
    {
      code: 'approval-reason',
      display: 'Approval declined reason',
      definition:
        'Why the participant or designated representative did not approve the recommended interventions',
      property: [
        {
          code: 'domain',
          valueCode: 'care-plan',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nar' },
      ],
    },
    {
      code: 'flooring-hazards-other',
      display: 'Other flooring hazard',
      definition: 'Other flooring/carpeting hazard, please specify:',
      property: [
        {
          code: 'domain',
          valueCode: 'environment',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nar' },
      ],
    },
    {
      code: 'bathroom-hazards-other',
      display: 'Other bathroom hazard',
      definition: 'Other bathroom/toilet hazard, please specify:',
      property: [
        {
          code: 'domain',
          valueCode: 'environment',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nar' },
      ],
    },
    {
      code: 'bedroom-hazards-other',
      display: 'Other bedroom hazard',
      definition: 'Other bedroom hazard, please specify:',
      property: [
        {
          code: 'domain',
          valueCode: 'environment',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nar' },
      ],
    },
    {
      code: 'kitchen-hazards-other',
      display: 'Other kitchen hazard',
      definition: 'Other kitchen hazard, please specify:',
      property: [
        {
          code: 'domain',
          valueCode: 'environment',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nar' },
      ],
    },
    {
      code: 'cold-storage-other',
      display: 'Other cold storage issue',
      definition: 'Other cold food storage issue, please specify:',
      property: [
        {
          code: 'domain',
          valueCode: 'environment',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nar' },
      ],
    },
    {
      code: 'heating-hazards-other',
      display: 'Other heating or cooling hazard',
      definition: 'Other heating/cooling hazard, please specify:',
      property: [
        {
          code: 'domain',
          valueCode: 'environment',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nar' },
      ],
    },
    {
      code: 'personal-safety-other',
      display: 'Other personal safety issue',
      definition: 'Other personal safety issue, please specify:',
      property: [
        {
          code: 'domain',
          valueCode: 'environment',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nar' },
      ],
    },
    {
      code: 'home-access-other',
      display: 'Other home access issue',
      definition: 'Other home access issue, please specify:',
      property: [
        {
          code: 'domain',
          valueCode: 'environment',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nar' },
      ],
    },
    {
      code: 'pet-issues-other',
      display: 'Other pet issue',
      definition: 'Other pet issue, please specify:',
      property: [
        {
          code: 'domain',
          valueCode: 'environment',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nar' },
      ],
    },
    {
      code: 'infestations-other',
      display: 'Other infestation',
      definition: 'Other bug or rodent infestation, please specify:',
      property: [
        {
          code: 'domain',
          valueCode: 'environment',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nar' },
      ],
    },
    {
      code: 'learning-barriers-other',
      display: 'Other learning barrier',
      definition: 'Other learning barrier, please specify:',
      property: [
        {
          code: 'domain',
          valueCode: 'education',
        },
        {
          code: 'observation-extract',
          valueBoolean: false,
        },
        {
          code: 'managed-by',
          valueCode: 'code',
        },
        { code: 'SCALE_TYP', valueString: 'Nar' },
      ],
    },
    ...PCP_QUESTION_CONCEPTS,
    ...RD_QUESTION_CONCEPTS,
    ...ASSESSMENT_TEMPLATE_QUESTION_CONCEPTS,
    ...INSTRUMENT_QUESTION_CONCEPTS,
    ...NUTRITION_QUESTION_CONCEPTS,
    ...VITAL_QUESTION_CONCEPTS,
  ],
}
